import { AddTaskRounded, CheckCircleOutlined, VisibilityOffRounded } from "@mui/icons-material";
import { Button, Link, Skeleton, Stack, Tooltip, Typography, useTheme } from "@mui/material";
import { useState } from "react";
import { CreateTaskItemModal, isWhitespace, ItemProperty, TaskItemStatusPicker } from "src/components";
import { DEFAULT_TASK_ITEM_STATUS, Links, TaskItemPrivacyType } from "src/constants";
import { TaskItemDetailDto, useGetTaskItemSubtasks } from "src/http";

interface Props {
  taskItem: TaskItemDetailDto;
  onClick?: (taskItemGuid: string) => void;
}

const TaskItemSubtasksProperty = (props: Props) => {
  const { data, isLoading } = useGetTaskItemSubtasks(props.taskItem.guid);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const theme = useTheme();

  function handleClicked(event: React.MouseEvent, taskItemGuid: string) {
    if (props.onClick) {
      event.preventDefault();
      props.onClick(taskItemGuid);
    }
  }

  return (
    <ItemProperty labelIcon={<CheckCircleOutlined />} label="Subtasks" labelProps={{ sx: { top: 1 } }}>
      {isLoading && (
        <Stack spacing={0.5} sx={{ mb: 1 }}>
          {[110, 180, 145].map((width, i) => (
            <Skeleton key={i} variant="rectangular" height={24} width={width} />
          ))}
        </Stack>
      )}
      {!isLoading && data && data.subtasks.length > 0 && (
        <Stack spacing={0.5} sx={{ mb: 1 }}>
          {data.subtasks.map((subtask) => (
            <Stack key={subtask.taskItemGuid} direction="row">
              <TaskItemStatusPicker
                taskItemGuid={subtask.taskItemGuid}
                status={subtask.currentStatus ?? DEFAULT_TASK_ITEM_STATUS}
                ownerUserGuids={[]}
                variant="button"
                disabled
              />
              <Stack direction="row" spacing={0.25} sx={{
                alignItems: "center"
              }}>
                <Tooltip
                  title={
                    <Typography>
                      {subtask.team && (
                        <>
                          <strong>{subtask.team.name}</strong>
                          <span> : </span>
                        </>
                      )}
                      <Link href={Links.TaskItemDetail(subtask.taskItemGuid)}>{subtask.description}</Link>
                    </Typography>
                  }
                  placement="right"
                >
                  <Link
                    href={Links.TaskItemDetail(subtask.taskItemGuid)}
                    onClick={(e) => handleClicked(e, subtask.taskItemGuid)}
                  >
                    {subtask.description == null || isWhitespace(subtask.description) ? (
                      <Typography variant="placeholder">Nothing here yet.</Typography>
                    ) : (
                      <Typography>{subtask.description}</Typography>
                    )}
                  </Link>
                </Tooltip>

                {subtask.privacy === TaskItemPrivacyType.Limited && (
                  <Tooltip
                    title={
                      <Stack>
                        <Typography>Limited</Typography>
                        <Typography variant="caption" sx={{ lineHeight: "1rem" }}>
                          Visible only to owners and assigned team members.
                        </Typography>
                      </Stack>
                    }
                    slotProps={{
                      tooltip: {
                        sx: {
                          color: theme.palette.error.main,
                        },
                      },
                    }}
                  >
                    <VisibilityOffRounded sx={{ color: theme.palette.error.main }} />
                  </Tooltip>
                )}
              </Stack>
            </Stack>
          ))}
        </Stack>
      )}

      <Button
        variant="tertiary"
        disabled={isLoading}
        startIcon={<AddTaskRounded />}
        onClick={() => setIsModalVisible(true)}
        sx={{ alignSelf: "start" }}
      >
        Add
      </Button>

      <CreateTaskItemModal
        visible={isModalVisible}
        parentTask={{ taskItemGuid: props.taskItem.guid, description: props.taskItem.description }}
        onCreated={() => setIsModalVisible(false)}
        onCancelled={() => setIsModalVisible(false)}
      />
    </ItemProperty>
  );
};

export { TaskItemSubtasksProperty };
